import React, { useState } from 'react';
import { SuggestionData } from './ClosedBetaWizard';
import { AttachmentDropzone } from './AttachmentDropzone';

interface StepSuggestionProps {
  initialData?: SuggestionData;
  onSubmit: (data: SuggestionData) => void;
  onBack: () => void;
  attachments: File[];
  onAttachmentsChange: (files: File[]) => void;
}

export const StepSuggestion: React.FC<StepSuggestionProps> = ({
  initialData,
  onSubmit,
  onBack,
  attachments,
  onAttachmentsChange
}) => {
  const [formData, setFormData] = useState<SuggestionData>({
    type: 'SUGGESTION',
    title: initialData?.title || '',
    suggestion: initialData?.suggestion || '',
    impact: initialData?.impact || '',
    context: initialData?.context || ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (formData.title.length > 140) newErrors.title = 'Title must be 140 characters or less';
    if (!formData.suggestion.trim()) newErrors.suggestion = 'Suggestion is required';
    if (!formData.impact) newErrors.impact = 'Impact level is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
          Title * ({formData.title.length}/140)
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Brief summary of your suggestion"
          maxLength={140}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
        {errors.title && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.title}</p>}
      </div>

      {/* Suggestion/Idea */}
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
          Suggestion/Idea *
        </label>
        <textarea
          value={formData.suggestion}
          onChange={e => setFormData(prev => ({ ...prev, suggestion: e.target.value }))}
          placeholder="Describe your feature suggestion or improvement idea in detail..."
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Be as specific as possible. Include details about how the feature should work and why it would be valuable.
        </p>
        {errors.suggestion && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.suggestion}</p>}
      </div>

      {/* Impact Level */}
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
          Expected Impact *
        </label>
        <div className="space-y-2">
          {[
            { 
              value: 'nice-to-have', 
              label: 'Nice to Have', 
              description: 'Would be a small improvement but not essential',
              color: 'bg-gray-100 dark:bg-gray-700' 
            },
            { 
              value: 'helpful', 
              label: 'Helpful', 
              description: 'Would meaningfully improve the user experience',
              color: 'bg-blue-100 dark:bg-blue-900' 
            },
            { 
              value: 'high-impact', 
              label: 'High Impact', 
              description: 'Would significantly enhance productivity or solve a major pain point',
              color: 'bg-green-100 dark:bg-green-900' 
            }
          ].map(impact => (
            <button
              key={impact.value}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, impact: impact.value }))}
              className={`w-full p-4 rounded-lg text-left transition-colors ${
                formData.impact === impact.value
                  ? `${impact.color} ring-2 ring-blue-500`
                  : `${impact.color} hover:ring-1 hover:ring-gray-300`
              }`}
            >
              <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                {impact.label}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {impact.description}
              </div>
            </button>
          ))}
        </div>
        {errors.impact && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.impact}</p>}
      </div>

      {/* Additional Context */}
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
          Additional Context
        </label>
        <textarea
          value={formData.context}
          onChange={e => setFormData(prev => ({ ...prev, context: e.target.value }))}
          placeholder="Any additional context, use cases, or examples that would help explain your suggestion..."
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>

      {/* Attachments */}
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
          Screenshots/Mockups
        </label>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          Optional: Upload screenshots or mockups to help illustrate your suggestion
        </p>
        <AttachmentDropzone
          attachments={attachments}
          onAttachmentsChange={onAttachmentsChange}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
        >
          Back
        </button>
        <button
          type="submit"
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          Continue to Review
        </button>
      </div>
    </form>
  );
}; 